import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { ArrowLeft, Users, TrendingUp, BarChart3, Target, Activity, AlertTriangle } from 'lucide-react';
import { V1Team, V1StandingEntry, LeagueDraftAnalytics } from '../../types';
import { v1Api } from '../../services/api';
import Card from '../common/Card';
import Button from '../common/Button';
import Skeleton from '../common/Skeleton';
import GradeStamp from '../common/GradeStamp';
import GradingLoader from '../common/GradingLoader';
import RoastComment from '../common/RoastComment';
import AnimatedNumber from '../common/AnimatedNumber';
import {
  computeDraftGrade,
  computeLuckGrade,
  computeSeasonGrade,
  LetterGrade,
} from '../../utils/gradeEngine';
import { getRoastComment, RoastMetrics } from '../../utils/roastEngine';

interface TeamLayoutProps {
  children?: React.ReactNode;
}

interface TeamWeeklyPerformance {
  team_id: number;
  team_name: string;
  week_range: { start: number; end: number };
  weekly_performance: Array<{
    week: number;
    total_points: number;
    starter_points: number;
    bench_points: number;
    projected_points: number;
    projection_diff: number;
    starters_count: number;
    bench_count: number;
    matchup?: {
      opponent_id: number;
      team_score: number;
      opponent_score: number;
      result: 'win' | 'loss' | 'tie';
      margin: number;
    };
  }>;
  summary: {
    total_weeks: number;
    avg_points: number;
    total_points: number;
    best_week: { week: number; points: number };
    worst_week: { week: number; points: number };
  };
}

export interface TeamContextData {
  team: V1Team | null;
  weeklyPerformance: TeamWeeklyPerformance | null;
  teamStanding: V1StandingEntry | null;
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

export const TeamContext = React.createContext<TeamContextData | null>(null);

const TeamLayout: React.FC<TeamLayoutProps> = ({ children }) => {
  const { teamId, espnLeagueId } = useParams<{ teamId: string; espnLeagueId: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  
  const [team, setTeam] = useState<V1Team | null>(null);
  const [weeklyPerformance, setWeeklyPerformance] = useState<TeamWeeklyPerformance | null>(null);
  const [teamStanding, setTeamStanding] = useState<V1StandingEntry | null>(null);
  const [allStandings, setAllStandings] = useState<V1StandingEntry[]>([]);
  const [draftAnalytics, setDraftAnalytics] = useState<LeagueDraftAnalytics | null>(null);
  // Roast + grades wait for this so they render once, with full evidence
  const [analyticsSettled, setAnalyticsSettled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchTeamData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const teamIdNum = parseInt(teamId!);
      
      // First get team data to get the league ID
      const teamData = await v1Api.teams.getById(teamIdNum);
      
      const [, performanceData, standingsData] = await Promise.all([
        v1Api.teams.getRoster(teamIdNum),
        v1Api.teams.getWeeklyPerformance(teamIdNum),
        v1Api.analytics.getStandings(teamData.league.id)
      ]);
      
      setTeam(teamData);
      setWeeklyPerformance(performanceData);

      // Find this team's standing
      const teamStandingEntry = standingsData?.standings?.find((s: V1StandingEntry) => s.team_id === teamIdNum);
      setTeamStanding(teamStandingEntry || null);
      setAllStandings(standingsData?.standings || []);

      // Grades load lazily — never gates the page render
      v1Api.leagues
        .getLeagueDraftAnalytics(teamData.league.id)
        .then(setDraftAnalytics)
        .catch((analyticsErr) => {
          console.error('Draft analytics fetch error:', analyticsErr);
        })
        .finally(() => setAnalyticsSettled(true));
    } catch (err) {
      setError('Failed to load team data');
      console.error('Team fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, [teamId]);

  useEffect(() => {
    if (teamId) {
      fetchTeamData();
    }
  }, [teamId, fetchTeamData]);

  // Determine active section from URL
  const activeSection = location.pathname.split('/').pop() || 'roster';

  // ── Grades (all derived from data already in responses; null until loaded) ──
  const teamDraftAnalytics = useMemo(
    () => draftAnalytics?.teams.find((t) => t.team_id === parseInt(teamId ?? '0')) ?? null,
    [draftAnalytics, teamId]
  );

  const draftGrade: LetterGrade | null = useMemo(
    () =>
      teamDraftAnalytics
        ? computeDraftGrade(teamDraftAnalytics.draft_performance.avg_value_score)
        : null,
    [teamDraftAnalytics]
  );

  const luckGrade: LetterGrade | null = useMemo(() => {
    if (!teamStanding || allStandings.length === 0) return null;
    return computeLuckGrade({
      wins: teamStanding.regular_season.wins,
      losses: teamStanding.regular_season.losses,
      pointsFor: teamStanding.regular_season.points_for,
      pointsAgainst: teamStanding.regular_season.points_against,
      leaguePointsFor: allStandings.map((s) => s.regular_season.points_for),
      leaguePointsAgainst: allStandings.map((s) => s.regular_season.points_against),
    });
  }, [teamStanding, allStandings]);

  const seasonGrade: LetterGrade | null = useMemo(() => {
    if (!draftGrade || !luckGrade || !teamStanding || allStandings.length === 0) return null;
    const winPercentile =
      allStandings.length > 1
        ? (allStandings.length - teamStanding.rank) / (allStandings.length - 1)
        : 0.5;
    return computeSeasonGrade({ draftGrade, winPercentile, luckGrade });
  }, [draftGrade, luckGrade, teamStanding, allStandings]);

  const roast: string | null = useMemo(() => {
    // Wait for draft analytics (success or failure) — the roast renders once,
    // with the full evidence, instead of flashing a data-starved fallback.
    if (!team || !teamStanding || !analyticsSettled) return null;
    const leagueAvgPointsFor =
      allStandings.length > 0
        ? allStandings.reduce((sum, s) => sum + s.regular_season.points_for, 0) / allStandings.length
        : undefined;
    const worstPick = teamDraftAnalytics?.worst_picks[0];
    const bestPick = teamDraftAnalytics?.best_picks[0];
    const metrics: RoastMetrics = {
      avgValueScore: teamDraftAnalytics?.draft_performance.avg_value_score,
      draftGrade: draftGrade ?? undefined,
      worstPickName: worstPick?.player_name,
      worstPickRound: worstPick?.draft_round,
      bestPickName: bestPick?.player_name,
      bestPickRound: bestPick?.draft_round,
      wins: teamStanding.regular_season.wins,
      losses: teamStanding.regular_season.losses,
      pointsFor: teamStanding.regular_season.points_for,
      pointsAgainst: teamStanding.regular_season.points_against,
      leagueAvgPointsFor,
      rank: teamStanding.rank,
      totalTeams: allStandings.length || undefined,
      acquisitions: team.roster_management?.acquisitions,
      trades: team.roster_management?.trades,
    };
    return getRoastComment(metrics, team.id, team.league.season);
  }, [team, teamStanding, allStandings, teamDraftAnalytics, draftGrade, analyticsSettled]);

  if (!teamId) {
    return (
      <div className="min-h-screen bg-background-primary flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-text-primary">Invalid team ID</h2>
          <Link to="/">
            <Button className="mt-4">Back to Home</Button>
          </Link>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-background-primary">
        <div className="container mx-auto px-4 py-8">
          <Skeleton className="h-12 w-80 mb-8" />
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            {[...Array(4)].map((_, i) => (
              <Skeleton key={i} className="h-32" />
            ))}
          </div>
          <GradingLoader label="Grading this team…" size="lg" className="py-16" />
        </div>
      </div>
    );
  }

  if (error || !team) {
    return (
      <div className="min-h-screen bg-background-primary flex items-center justify-center">
        <Card className="p-6 text-center">
          <div className="text-accent-danger mb-4 flex items-center justify-center gap-2">
            <AlertTriangle className="w-5 h-5" />
            {error || 'Failed to load team'}
          </div>
          <Button onClick={fetchTeamData} className="mr-4">Retry</Button>
          <Link to="/">
            <Button variant="secondary">Back to Home</Button>
          </Link>
        </Card>
      </div>
    );
  }

  const contextValue: TeamContextData = {
    team,
    weeklyPerformance,
    teamStanding,
    loading,
    error,
    refetch: fetchTeamData
  };

  return (
    <TeamContext.Provider value={contextValue}>
      <div className="min-h-screen bg-background-primary">
        <div className="container mx-auto px-4 py-8">
          {/* Header — the report card cover page */}
          <div className="mb-8">
            <button
              onClick={() => {
                navigate(`/leagues/espn/${espnLeagueId}/overview`);
              }}
              className="inline-flex items-center text-text-secondary hover:text-text-primary transition-colors mb-4"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to League Overview
            </button>

            <div className="flex items-start justify-between gap-6">
              <div className="min-w-0">
                <h1 className="text-4xl font-bold text-text-primary mb-2">{team.name}</h1>
                <div className="flex items-center space-x-6 text-text-secondary">
                  {team.owner_names && team.owner_names.length > 0 && (
                    <span className="flex items-center">
                      <Users className="w-4 h-4 mr-1" />
                      <span className="text-lg font-medium text-text-primary">
                        {team.owner_names.join(', ')}
                      </span>
                    </span>
                  )}
                </div>
                {roast && (
                  <RoastComment comment={roast} grade={seasonGrade} className="mt-4 max-w-xl" />
                )}
              </div>
              <GradeStamp
                grade={seasonGrade}
                size="lg"
                animate
                label="Season"
                className="flex-shrink-0"
              />
            </div>
          </div>

          {/* Stats Cards — the standing card leads, everything else stays compact */}
          <div className="grid grid-cols-1 md:grid-cols-5 gap-6 mb-8">
            {/* Final Standing + category grades */}
            {teamStanding && (
              <Card className="p-6 md:col-span-2">
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <p className="text-text-secondary text-sm">Final Standing</p>
                    <p className="text-3xl font-bold font-mono text-text-primary">
                      #{teamStanding.rank}
                    </p>
                    <p className="text-sm text-text-secondary">
                      {teamStanding.total_season.wins}-{teamStanding.total_season.losses} ({(teamStanding.total_season.win_percentage * 100).toFixed(1)}%)
                    </p>
                  </div>
                  {/* Category grades — only the ones we can actually compute */}
                  <div className="flex items-center gap-4">
                    <GradeStamp grade={draftGrade} size="sm" label="Draft" />
                    <GradeStamp grade={luckGrade} size="sm" label="Luck" />
                  </div>
                </div>
              </Card>
            )}

            {/* Regular Season Standing */}
            {teamStanding && (
              <Card className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-text-secondary text-sm">Reg. Season</p>
                    <p className="text-2xl font-bold font-mono text-text-primary">
                      #{teamStanding.regular_season.rank}
                    </p>
                    <p className="text-sm text-text-secondary">
                      {teamStanding.regular_season.wins}-{teamStanding.regular_season.losses}
                    </p>
                  </div>
                  <BarChart3 className="w-8 h-8 text-accent-primary" />
                </div>
              </Card>
            )}

            {/* Regular Season Points For */}
            {teamStanding && (
              <Card className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-text-secondary text-sm">Points For</p>
                    <p className="text-2xl font-bold font-mono text-text-primary">
                      <AnimatedNumber value={Number(teamStanding.regular_season.points_for)} />
                    </p>
                    <p className="text-sm text-text-secondary">
                      {Number(teamStanding.regular_season.avg_points_for || 0).toFixed(1)} per game
                    </p>
                  </div>
                  <Target className="w-8 h-8 text-accent-primary" />
                </div>
              </Card>
            )}

            {/* Points Against — the bad-luck stat gets the red pen */}
            {teamStanding && (
              <Card className="p-6 border-l-2 border-l-accent-danger">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-text-secondary text-sm">Points Against</p>
                    <p className="text-2xl font-bold font-mono text-accent-danger">
                      <AnimatedNumber value={Number(teamStanding.regular_season.points_against)} />
                    </p>
                    <p className="text-sm text-text-secondary">
                      schedule damage taken
                    </p>
                  </div>
                  <Activity className="w-8 h-8 text-accent-danger" />
                </div>
              </Card>
            )}
          </div>

          {/* Tab Navigation */}
          <div className="mb-8">
            <div className="flex space-x-1 bg-background-secondary p-1 rounded-lg w-fit border border-fieldline">
              {[
                { key: 'roster', label: 'Roster', icon: Users },
                { key: 'performance', label: 'Performance', icon: TrendingUp }
              ].map(({ key, label, icon: Icon }) => (
                <Link
                  key={key}
                  to={`/leagues/espn/${espnLeagueId}/teams/${teamId}/${key}`}
                  className={`px-4 py-2 rounded-md flex items-center space-x-2 transition-colors ${
                    activeSection === key
                      ? 'bg-accent-primary/15 text-accent-primary font-medium border-b-2 border-accent-primary'
                      : 'text-text-secondary hover:text-text-primary hover:bg-background-hover'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  <span>{label}</span>
                </Link>
              ))}
            </div>
          </div>

          {/* Outlet for nested routes */}
          <Outlet />
        </div>
      </div>
    </TeamContext.Provider>
  );
};

export default TeamLayout;